import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input, { Textarea, Select } from '../common/Input';
import Button from '../common/Button';
import ImageUpload from './ImageUpload';
import { AnuncioFormData, CATEGORIAS, ESTADOS_BRASIL, Caracteristica, Categoria } from '../../types';
import { useAnuncioForm } from '../../hooks/useAnuncios';

const STEPS = [
  { id: 1, title: 'Categoria', icon: '📂' },
  { id: 2, title: 'Informações', icon: '📝' },
  { id: 3, title: 'Localização', icon: '📍' },
  { id: 4, title: 'Detalhes', icon: '🔍' },
  { id: 5, title: 'Fotos', icon: '📷' },
];

const AnuncioForm: React.FC = () => {
  const navigate = useNavigate();
  const { criarAnuncio } = useAnuncioForm();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<AnuncioFormData>({
    titulo: '',
    descricao: '',
    preco: 0,
    precoNegociavel: false,
    categoria: '',
    subcategoria: '',
    cidade: '',
    estado: '',
    cep: '',
    caracteristicas: [],
    imagens: [],
  });

  const selectedCategoria = CATEGORIAS.find((c) => c.id === formData.categoria);

  const updateFormData = (updates: Partial<AnuncioFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
    // Clear related errors
    Object.keys(updates).forEach((key) => {
      if (errors[key]) {
        setErrors((prev) => ({ ...prev, [key]: '' }));
      }
    });
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 1:
        if (!formData.categoria) newErrors.categoria = 'Selecione uma categoria';
        break;
      case 2:
        if (!formData.titulo.trim()) newErrors.titulo = 'Título é obrigatório';
        else if (formData.titulo.length < 10) newErrors.titulo = 'Título deve ter pelo menos 10 caracteres';
        if (!formData.descricao.trim()) newErrors.descricao = 'Descrição é obrigatória';
        else if (formData.descricao.length < 50) newErrors.descricao = 'Descrição deve ter pelo menos 50 caracteres';
        if (!formData.preco || formData.preco <= 0) newErrors.preco = 'Preço deve ser maior que zero';
        break;
      case 3:
        if (!formData.estado) newErrors.estado = 'Estado é obrigatório';
        if (!formData.cidade.trim()) newErrors.cidade = 'Cidade é obrigatória';
        break;
      case 4:
        // Optional step, no required validations
        break;
      case 5:
        if (formData.imagens.length === 0) newErrors.imagens = 'Adicione pelo menos uma foto';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 5));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) return;

    setIsSubmitting(true);
    try {
      const anuncio = await criarAnuncio(formData);
      navigate(`/anuncios/${anuncio.id}`);
    } catch (error) {
      // Error handled by hook
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPrice = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (!numbers) return '';
    return Number(numbers).toLocaleString('pt-BR');
  };

  const addCaracteristica = () => {
    updateFormData({
      caracteristicas: [...formData.caracteristicas, { nome: '', valor: '' }],
    });
  };

  const updateCaracteristica = (index: number, field: keyof Caracteristica, value: string) => {
    const newCaracteristicas = [...formData.caracteristicas];
    newCaracteristicas[index] = { ...newCaracteristicas[index], [field]: value };
    updateFormData({ caracteristicas: newCaracteristicas });
  };

  const removeCaracteristica = (index: number) => {
    updateFormData({
      caracteristicas: formData.caracteristicas.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Stepper */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold transition-all ${
                    currentStep >= step.id
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {currentStep > step.id ? (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    step.icon
                  )}
                </div>
                <span className={`text-xs mt-2 font-medium hidden sm:block ${
                  currentStep >= step.id ? 'text-green-700' : 'text-gray-500'
                }`}>
                  {step.title}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`flex-1 h-1 mx-2 rounded ${
                  currentStep > step.id ? 'bg-green-600' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
        {/* Step 1: Categoria */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Escolha a Categoria</h2>
              <p className="text-gray-600">Selecione a categoria que melhor descreve seu anúncio</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {CATEGORIAS.map((categoria) => (
                <label
                  key={categoria.id}
                  className={`relative flex flex-col items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.categoria === categoria.id
                      ? 'border-green-600 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="categoria"
                    value={categoria.id}
                    checked={formData.categoria === categoria.id}
                    onChange={(e) => updateFormData({ categoria: e.target.value as Categoria, subcategoria: '' })}
                    className="sr-only"
                  />
                  <span className="text-4xl mb-2">{categoria.icone}</span>
                  <span className="font-semibold text-gray-900 text-center">{categoria.nome}</span>
                  {formData.categoria === categoria.id && (
                    <span className="absolute top-2 right-2 w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                  )}
                </label>
              ))}
            </div>
            {errors.categoria && (
              <p className="text-sm text-red-600">{errors.categoria}</p>
            )}

            {selectedCategoria && selectedCategoria.subcategorias.length > 0 && (
              <Select
                label="Subcategoria (opcional)"
                value={formData.subcategoria}
                onChange={(e) => updateFormData({ subcategoria: e.target.value })}
                options={selectedCategoria.subcategorias.map((sub) => ({ value: sub, label: sub }))}
                placeholder="Selecione uma subcategoria"
              />
            )}
          </div>
        )}

        {/* Step 2: Informações */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Informações do Anúncio</h2>
              <p className="text-gray-600">Descreva bem seu produto para atrair mais compradores</p>
            </div>

            <Input
              label="Título do anúncio"
              placeholder="Ex: Cavalo Quarto de Milha 5 anos - Dócil e treinado"
              value={formData.titulo}
              onChange={(e) => updateFormData({ titulo: e.target.value })}
              error={errors.titulo}
              helperText={`${formData.titulo.length}/100 caracteres`}
              maxLength={100}
              required
            />

            <Textarea
              label="Descrição"
              placeholder="Descreva seu produto em detalhes: características, estado, histórico, diferenciais..."
              value={formData.descricao}
              onChange={(e) => updateFormData({ descricao: e.target.value })}
              error={errors.descricao}
              helperText={`${formData.descricao.length}/2000 caracteres (mínimo 50)`}
              maxLength={2000}
              rows={6}
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Preço <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                    R$
                  </span>
                  <input
                    type="text"
                    value={formData.preco ? formatPrice(formData.preco.toString()) : ''}
                    onChange={(e) => {
                      const numbers = e.target.value.replace(/\D/g, '');
                      updateFormData({ preco: numbers ? Number(numbers) : 0 });
                    }}
                    className={`w-full pl-12 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none ${
                      errors.preco ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0"
                  />
                </div>
                {errors.preco && (
                  <p className="mt-1.5 text-sm text-red-600">{errors.preco}</p>
                )}
              </div>

              <div className="flex items-end pb-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.precoNegociavel}
                    onChange={(e) => updateFormData({ precoNegociavel: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-green-700 focus:ring-green-500"
                  />
                  <span className="text-gray-700 font-medium">Preço negociável</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Localização */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Localização</h2>
              <p className="text-gray-600">Informe onde está localizado o produto</p>
            </div>

            <Select
              label="Estado"
              value={formData.estado}
              onChange={(e) => updateFormData({ estado: e.target.value })}
              options={ESTADOS_BRASIL.map((e) => ({ value: e.sigla, label: e.nome }))}
              placeholder="Selecione o estado"
              error={errors.estado}
              required
            />

            <Input
              label="Cidade"
              placeholder="Digite o nome da cidade"
              value={formData.cidade}
              onChange={(e) => updateFormData({ cidade: e.target.value })}
              error={errors.cidade}
              required
            />

            <Input
              label="CEP (opcional)"
              placeholder="00000-000"
              value={formData.cep}
              onChange={(e) => {
                const cep = e.target.value.replace(/\D/g, '');
                const formatted = cep.length > 5 ? `${cep.slice(0, 5)}-${cep.slice(5, 8)}` : cep;
                updateFormData({ cep: formatted });
              }}
              maxLength={9}
            />
          </div>
        )}

        {/* Step 4: Detalhes/Características */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Características Adicionais</h2>
              <p className="text-gray-600">Adicione detalhes específicos do seu produto (opcional)</p>
            </div>

            <div className="space-y-4">
              {formData.caracteristicas.map((car, index) => (
                <div key={index} className="flex gap-3 items-start">
                  <div className="flex-1">
                    <Input
                      placeholder="Ex: Idade, Raça, Peso..."
                      value={car.nome}
                      onChange={(e) => updateCaracteristica(index, 'nome', e.target.value)}
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      placeholder="Ex: 5 anos, Quarto de Milha, 450kg..."
                      value={car.valor}
                      onChange={(e) => updateCaracteristica(index, 'valor', e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeCaracteristica(index)}
                    className="p-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))}

              <Button
                type="button"
                variant="outline"
                onClick={addCaracteristica}
                leftIcon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                }
              >
                Adicionar Característica
              </Button>
            </div>

            {/* Sugestões de características */}
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Sugestões de características:</p>
              <div className="flex flex-wrap gap-2">
                {['Idade', 'Raça', 'Pelagem', 'Altura', 'Peso', 'Registro', 'Vacinas em dia', 'Documentação'].map((sug) => (
                  <button
                    key={sug}
                    type="button"
                    onClick={() => {
                      if (!formData.caracteristicas.find((c) => c.nome === sug)) {
                        updateFormData({
                          caracteristicas: [...formData.caracteristicas, { nome: sug, valor: '' }],
                        });
                      }
                    }}
                    className="px-3 py-1.5 bg-white border border-gray-300 rounded-full text-sm text-gray-700 hover:border-green-500 hover:text-green-700 transition-colors"
                  >
                    + {sug}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Fotos */}
        {currentStep === 5 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Fotos do Anúncio</h2>
              <p className="text-gray-600">Adicione fotos de qualidade para destacar seu produto</p>
            </div>

            <ImageUpload
              images={formData.imagens}
              onChange={(images) => updateFormData({ imagens: images })}
              maxImages={10}
              error={errors.imagens}
            />

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <h4 className="font-semibold text-amber-800 mb-2">💡 Dicas para boas fotos:</h4>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• Use luz natural sempre que possível</li>
                <li>• Fotografe de vários ângulos</li>
                <li>• Mostre detalhes importantes</li>
                <li>• Evite fotos borradas ou escuras</li>
                <li>• A primeira foto será a capa do anúncio</li>
              </ul>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="ghost"
            onClick={prevStep}
            disabled={currentStep === 1}
            leftIcon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            }
          >
            Voltar
          </Button>

          {currentStep < 5 ? (
            <Button
              type="button"
              onClick={nextStep}
              rightIcon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              }
            >
              Continuar
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              leftIcon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              }
            >
              Publicar Anúncio
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnuncioForm;
